import React, { useState } from 'react';
import { ModalBase } from './ModalBase';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const legalAreas = [
  {
    id: 'traffic-fines',
    title: `Multas de Tráfico`,
    icon: 'directions_car',
    description: `Recurra de forma experta multas y sanciones de tráfico en toda Europa.`,
    longDescription: `Nuestro equipo se especializa en todo tipo de infracciones de tráfico, desde multas de velocidad captadas por cámaras hasta sanciones por cambios de carril indebidos. Analizamos la legalidad de la multa, verificamos errores de procedimiento y construimos una defensa sólida para impugnar la sanción. Gestionamos todo el proceso para que no tenga que preocuparse por la jerga legal ni los plazos.`
  },
  {
    id: 'admin-sanctions',
    title: `Sanciones Administrativas`,
    icon: 'gavel',
    description: `Impugne sanciones administrativas y penalizaciones oficiales.`,
    longDescription: `Más allá de las multas de tráfico, gestionamos una amplia gama de sanciones administrativas. Esto incluye penalizaciones relacionadas con normativas de transporte, infracciones medioambientales o licencias comerciales. Nuestros expertos legales revisarán su caso para asegurar que la sanción se aplicó correctamente y que sus derechos han sido respetados.`
  },
  {
    id: 'tax-hacienda',
    title: `Impuestos/Hacienda`,
    icon: 'account_balance',
    description: `Asesoramiento para resolver disputas y problemas fiscales.`,
    longDescription: `Ofrecemos asistencia experta en disputas con la administración tributaria. Ya sea una liquidación incorrecta, una sanción fiscal o un requerimiento de información, nuestro equipo le guiará a través del proceso para asegurar una resolución justa y favorable, protegiendo sus intereses financieros.`
  },
  {
    id: 'social-security',
    title: `Seguridad Social`,
    icon: 'security',
    description: `Soporte para asuntos y reclamaciones de la seguridad social.`,
    longDescription: `Le ayudamos a navegar por el complejo sistema de la Seguridad Social. Desde reclamaciones de prestaciones por incapacidad, jubilación o desempleo, hasta la impugnación de deudas o sanciones. Le ofrecemos el soporte necesario para garantizar que reciba las prestaciones que le corresponden.`
  },
  {
    id: 'consumer-issues',
    title: `Problemas de Consumo`,
    icon: 'shopping_cart',
    description: `Asistencia en quejas y disputas de consumidores.`,
    longDescription: `Defendemos sus derechos como consumidor. Si ha tenido problemas con un producto defectuoso, un servicio no conforme, cláusulas abusivas en un contrato o publicidad engañosa, le ayudamos a formular su reclamación y a buscar una compensación justa.`
  },
  {
    id: 'service-disputes',
    title: `Disputas con Empresas`,
    icon: 'business_center',
    description: `Resuelva disputas con proveedores de servicios y compañías.`,
    longDescription: `Intervenimos en conflictos con compañías de suministros (luz, agua, gas), telecomunicaciones o cualquier otro proveedor de servicios. Si le han facturado de más, le han cortado el servicio indebidamente o no cumplen con el contrato, nosotros le ayudamos a resolver la situación.`
  },
];

const faqs = [
  {
    question: `¿Qué posibilidades de éxito tengo al recurrir una multa?`,
    answer: `Las posibilidades son altas, especialmente si existen defectos de forma en la notificación, errores en los datos, o si las pruebas (como la foto del radar) no son claras. Nuestro equipo legal analiza cada caso para identificar los puntos débiles de la sanción y maximizar las probabilidades de éxito.`,
  },
  {
    question: `¿Cuánto tiempo tarda el proceso de recurso?`,
    answer: `El proceso puede variar dependiendo de la administración y el país. Generalmente, la fase de alegaciones iniciales puede tardar entre 1 y 3 meses. Si es necesario presentar recursos posteriores, el proceso puede extenderse. Te mantendremos informado en cada etapa.`,
  },
  {
    question: `¿Qué ocurre si finalmente no se gana el recurso?`,
    answer: `Si el recurso es desestimado, se deberá abonar la sanción. Sin embargo, en muchos casos, al iniciar el recurso se pierde el descuento por pronto pago. Evaluamos contigo los riesgos y beneficios antes de empezar, para que tomes una decisión informada.`,
  },
  {
    question: `¿Cubrís multas de países no comunitarios como Suiza o Reino Unido?`,
    answer: `Sí, gracias a nuestra red de colaboradores locales, también gestionamos multas en países europeos fuera de la UE como Suiza, Noruega y el Reino Unido. Las normativas son diferentes, pero contamos con expertos en cada jurisdicción.`,
  },
  {
    question: `¿Necesito enviaros la multa original?`,
    answer: `No es necesario. Para iniciar el proceso, basta con que nos envíes una copia escaneada o una fotografía de buena calidad de todas las páginas de la notificación a través de nuestro formulario de contacto seguro.`,
  },
];

const AccordionItem: React.FC<{ faq: { question: string, answer: string }, isOpen: boolean, onClick: () => void }> = ({ faq, isOpen, onClick }) => (
    <div className="border-b border-gray-200 py-4">
        <h2>
            <button
                type="button"
                className="flex justify-between items-center w-full text-left font-semibold text-gray-800 hover:text-blue-600"
                onClick={onClick}
                aria-expanded={isOpen}
            >
                <span>{faq.question}</span>
                <svg
                    className={`w-5 h-5 transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
        </h2>
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 mt-4' : 'max-h-0'}`}>
            <div className="prose prose-sm max-w-none text-gray-600">
                <p>{faq.answer}</p>
            </div>
        </div>
    </div>
);

export const FaqModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
    const [activeAreaId, setActiveAreaId] = useState<string | null>(null);

    const handleToggleFaq = (index: number) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };
    
    const handleSelectArea = (id: string) => setActiveAreaId(id);
    const handleGoBack = () => setActiveAreaId(null);
    
    const selectedArea = legalAreas.find(area => area.id === activeAreaId);
    
    const containerClassName = activeAreaId ? 'max-w-2xl' : 'max-w-5xl';

    return (
        <ModalBase isOpen={isOpen} onClose={onClose} title="Soporte Legal Integral" containerClassName={containerClassName}>
            <div className="bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 -m-8 p-8">
                <div className={`mx-auto ${activeAreaId ? 'text-left' : 'text-center'}`}>
                    {selectedArea ? (
                         <>
                            <div className="flex items-center gap-4 mb-6">
                                <div className="text-green-500 bg-green-100 p-3 rounded-full shadow-md">
                                    <span className="material-icons-outlined text-3xl">{selectedArea.icon}</span>
                                </div>
                                <h1 className="text-3xl font-bold text-blue-900 leading-tight">
                                    {selectedArea.title}
                                </h1>
                            </div>
                            <p className="text-blue-700 text-lg leading-relaxed">{selectedArea.longDescription}</p>
                            <button
                                onClick={handleGoBack}
                                className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors duration-200 text-sm font-medium"
                            >
                                <span className="material-icons-outlined text-base">arrow_back</span>
                                Volver a todos los servicios
                            </button>
                        </>
                    ) : (
                        <>
                            <h1 className="text-4xl sm:text-5xl font-bold text-blue-900 leading-tight mb-6">
                                Soporte Legal Integral
                            </h1>
                            <p className="text-blue-700 text-lg sm:text-xl leading-relaxed max-w-4xl mx-auto">
                                Multas.EU ofrece asistencia experta en multas de tráfico y sanciones administrativas en toda Europa. Nuestra plataforma le proporciona orientación específica para cada país, capacitándole con el conocimiento y los recursos para navegar eficazmente por sus derechos y opciones legales.
                            </p>
                        </>
                    )}
                </div>
            </div>

            {!selectedArea && (
                <div className="py-12">
                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                        {legalAreas.map(area => (
                            <div key={area.id} className="group flex flex-col gap-5 rounded-xl border border-blue-200 bg-white p-6 shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out transform hover:-translate-y-2 hover:border-green-500">
                                <div className="text-green-500 bg-green-100 p-4 rounded-full w-fit shadow-md">
                                    <span className="material-icons-outlined text-4xl">{area.icon}</span>
                                </div>
                                <div>
                                    <h2 className="text-blue-900 text-2xl font-semibold leading-tight mb-2">{area.title}</h2>
                                    <p className="text-blue-600 text-base font-normal leading-normal">{area.description}</p>
                                    <button onClick={() => handleSelectArea(area.id)} className="mt-4 inline-flex items-center text-base font-medium text-green-600 hover:text-green-700 group-hover:underline">
                                        Saber más <span className="material-icons-outlined text-base ml-1 transition-transform group-hover:translate-x-1">arrow_forward</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            <hr className="my-8 border-gray-200"/>

            <div className="px-4">
                <h2 className="text-center text-3xl font-bold text-gray-800 mb-6">Preguntas Frecuentes</h2>
                 <div className="space-y-2 max-w-3xl mx-auto">
                    {faqs.map((faq, index) => (
                        <AccordionItem
                            key={index}
                            faq={faq}
                            isOpen={openFaqIndex === index}
                            onClick={() => handleToggleFaq(index)}
                        />
                    ))}
                </div>
            </div>
        </ModalBase>
    );
};